<template>
  <BaseModal no-padding no-exit card-width="480px">
    <template #default>
      <div class="flex flex-col items-center justify-center w-full h-full gap-3 pt-1">
        <div class="rounded-md p-2 border-secondary-yellow-10 w-11 h-11" style="border-width: 1.5px;">
          <div class="bg-secondary-yellow-10 rounded-md p-1 w-full h-full flex items-center justify-center">
            <ConnectionIcon class="text-secondary-yellow-200" :width="18" :height="18" />
          </div>
        </div>
        <div class="space-y-3 text-center px-16">
          <BaseText type="label" size="sm" class="text-text-muted">
            Your {{ formattedPlatformName }} account has disconnected.
          </BaseText>
          <BaseText size="sm" class="text-text-normal">
            We lost connection to your {{ formattedPlatformName }} account. Please re-authenticate to continue pulling Lens data.
          </BaseText>
        </div>
        <div class="flex flex-col gap-1 w-full px-5 pb-5 mt-3" v-if="currentConnection?.connectionOwner">
          <BaseText type="label" size="xs" class="text-text-muted">
            Connection Created by:
          </BaseText>
          <div class="bg-background-normal rounded-md w-full flex items-center gap-2 p-3">
            <img v-if="currentConnection?.connectionOwner?.avatar" class="rounded-full w-5 h-5" :src="currentConnection?.connectionOwner?.avatar">
            <img v-else src="../../assets/images/defaultProfileImage.webp" class="rounded-full w-5 h-5">
            <BaseText type="label" size="sm" class="text-text-muted flex-1">
              {{ currentConnection?.connectionOwner?.name }}
            </BaseText>
            <BaseText size="sm" class="text-text-normal">
              {{ currentConnection?.connectionOwner?.email }}
            </BaseText>
          </div>
        </div>
        <!-- Footer -->
        <div class="p-5 w-full flex items-center justify-between border-t border-border-normal">
          <button class="px-3 py-1.5 rounded-md transition-colors duration-100 hover:bg-neutral-25 h-8"
          @click="$emit('close')">
            <BaseText type="label" size="sm" class="text-text-muted">
              Close
            </BaseText>
          </button>
          <button class="px-3 py-1.5 rounded-md black-btn-primary h-8 w-40 flex items-center justify-center relative group"
          :disabled="!isConnectionOwner || loading" @click="handleAuthenticate">
            <div v-if="!isConnectionOwner" class="btn-tooltip text-neutral-alpha-650 group-hover:opacity-100 opacity-0 transition-opacity pointer-events-none">
              <BaseText size="sm" class="p-2 text-left">
                The original account auth {{ currentConnection?.connectionOwner?.email ?? 'creator' }} needs to restore the connection.
              </BaseText>
            </div>
            <BaseLoadingSpinnerCircle v-if="loading" class="text-text-muted" />
            <BaseText v-else type="label" size="sm" :class="isConnectionOwner ? 'text-white' : 'text-text-disabled'">
              Restore Connection
            </BaseText>
          </button>
        </div>
      </div>
      <!-- Authentication Controller Interface -->
      <LensPlatformAuthenticator 
        ref="platformAuthenticator"
        :loading="loading"
      />
    </template>
  </BaseModal>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { reverseProviderKeyMap } from '@/utils/lens/dataSourceUtils'
import LensPlatformAuthenticator from './LensPlatformAuthenticator.vue'

// Icons
import ConnectionIcon from '../globals/Icons/ConnectionIcon.vue'

export default {
  name: 'LensConnectionLostModal',
  components: {
    LensPlatformAuthenticator,
    ConnectionIcon
  },
  props: {
    disconnectedProviders: {
      type: Array,
      default: () => []
    }
  },
  created () {
    this.unresolvedConnections = [...this.disconnectedProviders]
  },
  data () {
    return {
      unresolvedConnections: [],
      url: null,
      loading: false
    }
  },
  computed: {
    ...mapGetters('AuthModule', ['getUser']),
    currentConnection () {
      return this.unresolvedConnections?.[0] ?? null
    },
    isConnectionOwner () {
      return this.currentConnection?.user_id === this.getUser.user_id
    },
    connectionPlatform () {
      return reverseProviderKeyMap[this.currentConnection?.provider]
    },
    formattedPlatformName () {
      return this.connectionPlatform === 'meta' ? 'Meta' : 'TikTok'
    }
  },
  methods: {
    ...mapActions('LensModule', ['fetchSidebarLenses']),
    handleClose () {
      const newUnresolvedConnections = [...this.unresolvedConnections].shift()
      if (!newUnresolvedConnections.length) {
        this.$emit('close')
        return
      }
      this.unresolvedConnections = newUnresolvedConnections
    },
    async handleAuthenticate () {
      if (!this.isConnectionOwner) {
        this.$showAlert({
          message: 'You are not the connection owner.',
          type: 'error'
        })
        return
      }
      this.loading = true
      try {
        await this.$refs.platformAuthenticator.handlePlatformAuthenticate(this.connectionPlatform)
        await this.fetchSidebarLenses()
        this.handleClose()
      } catch (error) {
        console.error('Failed to authenticate platform:', error)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.btn-tooltip {
  position: absolute;
  top: calc(100% + 12px);
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(6, 7, 15, 0.92);
  padding: 5px 10px;
  border-radius: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  width: 220px;
}
</style>
